import * as React from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib'
import { typographyStyle } from '../lib/catalog'
import { designTokens } from '../lib/design-tokens'

type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> & {
  children?: ReactNode
  label?: ReactNode
  className?: string
  boxClassName?: string
  labelClassName?: string
  variant?: 'default' | 'videoFilterDark'
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      boxClassName,
      labelClassName,
      children,
      label,
      checked,
      defaultChecked,
      disabled,
      onChange,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(Boolean(defaultChecked))
    const isControlled = checked !== undefined
    const isChecked = isControlled ? Boolean(checked) : uncontrolledChecked
    const labelContent = label ?? children

    const boxStyle: CSSProperties & { [key in `--control-${string}`]?: string } = {
      '--control-ring': designTokens.colors.border.inputFocus,
      '--control-ring-offset': designTokens.colors.background.surface,
      width: designTokens.sizes.width.checkbox,
      height: designTokens.sizes.height.checkbox,
      minWidth: designTokens.sizes.width.checkbox,
      minHeight: designTokens.sizes.height.checkbox,
      borderRadius: designTokens.borders.radius.sm,
      borderWidth: designTokens.borders.widths.checkbox,
      borderStyle: designTokens.borders.styles.solid,
      borderColor:
        variant === 'videoFilterDark'
          ? '#5C5C5C'
          : isChecked
            ? designTokens.colors.border.actionPrimary
            : designTokens.colors.border.default,
      background:
        variant === 'videoFilterDark'
          ? '#404040'
          : isChecked
            ? designTokens.colors.background.actionPrimary
            : designTokens.colors.background.white,
    }

    const labelStyle: CSSProperties =
      variant === 'videoFilterDark'
        ? {
            color: designTokens.colors.text.inverse,
            ...typographyStyle(designTokens.typography.body.base, {
              lineHeight: '130%',
            }),
          }
        : {
            color: designTokens.colors.text.primary,
            ...typographyStyle(designTokens.typography.body.xsNormal),
          }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setUncontrolledChecked(event.target.checked)
      }
      onChange?.(event)
    }

    return (
      <label
        className={cn(
          'inline-flex items-center',
          !disabled && 'cursor-pointer',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
        style={{ gap: variant === 'videoFilterDark' ? '12px' : designTokens.spacing.xs }}
      >
        <input
          ref={ref}
          type="checkbox"
          className="peer sr-only"
          {...(isControlled ? { checked: isChecked } : { defaultChecked })}
          disabled={disabled}
          onChange={handleChange}
          {...props}
        />
        <span
          className={cn(
            'flex items-center justify-center transition-colors',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--control-ring)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--control-ring-offset)]',
            boxClassName
          )}
          style={boxStyle}
        >
          {isChecked && (
            <>
              {variant === 'videoFilterDark' ? (
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M13.3327 4L5.99935 11.3333L2.66602 8"
                    stroke="#F5F5F5"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3 w-3">
                  <path
                    d="M3.5 8.5L6.75 11.5L12.5 4.5"
                    stroke={designTokens.colors.text.white}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </>
          )}
        </span>
        {labelContent ? (
          <span className={cn('select-none', labelClassName)} style={labelStyle}>
            {labelContent}
          </span>
        ) : null}
      </label>
    )
  }
)
Checkbox.displayName = 'Checkbox'

export { Checkbox }
